import {
  Badge, Box, Button, Checkbox, Flex, Input, Select, Text, Textarea,
  Table, TableContainer, Tbody, Td, Th, Thead, Tr,
} from "@chakra-ui/react";
import { useEffect, useState, useReducer, useMemo } from "react";
import { api } from "../../../services/api";
import { useAuth } from "../../../Hooks/useAuth";
import { getEventCalculatorOptions } from "../../../services/eventCalculator";
import { BatchCard } from "../BatchCard";
import { EditProduct } from "../EditProduct";
import { FiPlus, FiX, FiTrash2, FiSearch } from "react-icons/fi";
import swal from "sweetalert";

const GREEN  = "#004a33";
const ORANGE = "#fc8f04";

function FormField({ label, children }) {
  return (
    <Box>
      <Text fontSize="11px" fontWeight="700" color="gray.500" mb="4px" textTransform="uppercase" letterSpacing="0.05em">{label}</Text>
      {children}
    </Box>
  );
}

const inputProps = {
  borderRadius: "8px",
  fontSize: "13px",
  bg: "white",
  border: "1px solid rgba(0,74,51,.2)",
  _focus: { borderColor: GREEN, boxShadow: `0 0 0 1px ${GREEN}` },
};

export function ProductCard({ allProducts, allProductTypes, allCategories, allSubcategories, allBatches, allPrices }) {
  const { user } = useAuth();
  const [products, setProducts] = useState(allProducts);
  const [productTypes] = useState(allProductTypes);
  const [product, setProduct] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    { name: "", description: "", brand: "", size: "", quantity_per_box: "", market_price: "", product_type_id: "", suggested_price_for_sale: "", customer_review: "", unit_volume_ml: "", is_official_kit: false, event_drink_category: "" }
  );
  const [productPhotos, setProductPhotos] = useState([]);
  const [productFlyer, setProductFlyer] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState("");
  const [drinkCategories, setDrinkCategories] = useState([]);
  const [subcategoryFilter, setSubcategoryFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;

  // Correction C12 (briefing v5): lets a single product override the
  // ProductType-level calculator category — the destilado curation case
  // (only 2-3 gin/vodka/cachaça/licor products should show up in the
  // calculator, while their ProductType stays unmapped). Same source as the
  // ProductType form (TypeCard/EditProductType), fetched independently here
  // since this card doesn't share state with that one.
  useEffect(() => {
    async function fetchDrinkCategories() {
      try {
        const response = await getEventCalculatorOptions();
        setDrinkCategories(response.data.catalog_drink_categories || []);
      } catch (error) {
        console.error("Error fetching catalog drink categories:", error);
      }
    }
    fetchDrinkCategories();
  }, []);

  // Full chain Produto -> Tipo -> Categoria -> Subcategoria, plus how THIS
  // product will actually resolve in the event calculator: its own override
  // if set, otherwise the Tipo's category, otherwise none. Surfacing this
  // whole taxonomy (not just the calculator category) is what lets an admin
  // catch a wrong Tipo/Categoria at a glance instead of only after a product
  // fails to show up where expected on the storefront or the calculator.
  const productTypeById = useMemo(
    () => Object.fromEntries(productTypes.map(pt => [pt.id, pt])),
    [productTypes]
  );
  const categoryById = useMemo(
    () => Object.fromEntries((allCategories || []).map(c => [c.id, c])),
    [allCategories]
  );
  const subcategoryById = useMemo(
    () => Object.fromEntries((allSubcategories || []).map(s => [s.id, s])),
    [allSubcategories]
  );
  const drinkCategoryLabel = (key) => drinkCategories.find(dc => dc.key === key)?.label || key;

  function resolveTaxonomy(p) {
    const productType = productTypeById[p.product_type_id];
    const category = categoryById[productType?.category_id];
    const subcategory = subcategoryById[category?.subcategory_id];
    const calculatorKey = p.event_drink_category || productType?.event_drink_category || null;
    const calculatorSource = p.event_drink_category ? "produto" : productType?.event_drink_category ? "tipo" : null;
    return { productType, category, subcategory, calculatorKey, calculatorSource };
  }

  // Cascading filter options, following the real hierarchy (Subcategoria has
  // many Categorias, Categoria has many Tipos): picking a Subcategoria narrows
  // which Categorias make sense to offer next, and picking a Categoria narrows
  // the Tipo list — so the admin can't land on an impossible combination.
  const categoryOptions = useMemo(() => {
    const all = allCategories || [];
    if (!subcategoryFilter) return all;
    return all.filter(c => String(c.subcategory_id) === String(subcategoryFilter));
  }, [allCategories, subcategoryFilter]);

  const typeOptions = useMemo(() => {
    if (categoryFilter) return productTypes.filter(pt => String(pt.category_id) === String(categoryFilter));
    if (subcategoryFilter) {
      const categoryIds = new Set(categoryOptions.map(c => String(c.id)));
      return productTypes.filter(pt => categoryIds.has(String(pt.category_id)));
    }
    return productTypes;
  }, [productTypes, categoryFilter, subcategoryFilter, categoryOptions]);

  // Drop a now-invalid downstream selection when a broader filter changes
  // (e.g. switching Subcategoria while a Categoria from the old one is still
  // picked) instead of silently filtering against a combination that can
  // never match anything.
  useEffect(() => {
    if (categoryFilter && !categoryOptions.some(c => String(c.id) === String(categoryFilter))) {
      setCategoryFilter("");
    }
  }, [categoryOptions, categoryFilter]);
  useEffect(() => {
    if (typeFilter && !typeOptions.some(pt => String(pt.id) === String(typeFilter))) {
      setTypeFilter("");
    }
  }, [typeOptions, typeFilter]);

  const filteredProducts = useMemo(() => {
    let list = products;

    if (typeFilter) {
      list = list.filter(p => String(p.product_type_id) === String(typeFilter));
    } else if (categoryFilter) {
      list = list.filter(p => String(resolveTaxonomy(p).category?.id) === String(categoryFilter));
    } else if (subcategoryFilter) {
      list = list.filter(p => String(resolveTaxonomy(p).subcategory?.id) === String(subcategoryFilter));
    }

    if (search.trim()) {
      const q = search.toLowerCase().trim();
      list = list.filter(p =>
        p.name?.toLowerCase().includes(q) ||
        p.brand?.toLowerCase().includes(q) ||
        p.size?.toLowerCase().includes(q)
      );
    }

    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, search, typeFilter, categoryFilter, subcategoryFilter, productTypeById, categoryById, subcategoryById]);

  // Pure client-side pagination: the dashboard already fetched every product
  // in one payload (no server-side endpoint to page through), so this is
  // about cutting DOM/render weight — hundreds of table rows with 6+ cells
  // each is the part that actually gets sluggish, not the network request.
  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedProducts = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredProducts.slice(start, start + PAGE_SIZE);
  }, [filteredProducts, currentPage]);

  useEffect(() => {
    setPage(1);
  }, [search, typeFilter, categoryFilter, subcategoryFilter]);

  function clearFilters() {
    setSearch("");
    setTypeFilter("");
    setCategoryFilter("");
    setSubcategoryFilter("");
  }

  const handleChange = (name, value) => { setProduct({ [name]: value }); };

  async function handleCreate(e) {
    e.preventDefault();
    if (!product.name || !product.brand || !product.description || !product.product_type_id || !product.quantity_per_box) {
      swal({ title: "Atenção", text: "Nome, marca, descrição, tipo e quantidade por caixa são obrigatórios.", icon: "info" });
      return;
    }
    const formData = new FormData();
    formData.append("product[name]", product.name);
    formData.append("product[product_type_id]", product.product_type_id);
    formData.append("product[brand]", product.brand);
    formData.append("product[size]", product.size);
    formData.append("product[description]", product.description);
    formData.append("product[market_price]", product.market_price);
    formData.append("product[quantity_per_box]", product.quantity_per_box);
    formData.append("product[suggested_price_for_sale]", product.suggested_price_for_sale);
    formData.append("product[customer_review]", product.customer_review);
    if (product.unit_volume_ml) formData.append("product[unit_volume_ml]", product.unit_volume_ml);
    formData.append("product[is_official_kit]", !!product.is_official_kit);
    formData.append("product[event_drink_category]", product.event_drink_category || "");
    if (productFlyer) formData.append("product[flyer]", productFlyer);
    if (productPhotos) {
      for (let i = 0; i < productPhotos.length; i++) {
        formData.append("product[photos][]", productPhotos[i]);
      }
    }
    try {
      const response = await api.post("/api/v1/products", formData, {
        headers: { "access-token": user.token, client: user.client, uid: user.uid, accept: "application/json", contentType: "multipart/form-data" },
      });
      if (response.status === 200) {
        setProducts(prev => [...prev, response.data]);
        setShowForm(false);
        swal({ title: "Criado!", text: "Produto cadastrado com sucesso.", icon: "success" });
      } else {
        swal({ title: "Erro", text: "Algo deu errado.", icon: "error" });
      }
    } catch {
      swal({ title: "Erro", text: "Erro ao cadastrar produto.", icon: "error" });
    }
  }

  async function handleRemove(e, productId) {
    e.preventDefault();
    const confirmed = window.confirm("Remover este produto? Esta ação não pode ser desfeita.");
    if (!confirmed) return;
    try {
      const response = await api.delete(`/api/v1/products/${productId}`);
      if (response.status === 204) {
        setProducts(prev => prev.filter(p => p.id !== productId));
        swal({ title: "Removido!", text: "Produto removido com sucesso.", icon: "success" });
      } else {
        swal({ title: "Erro", text: "Algo deu errado.", icon: "error" });
      }
    } catch {
      swal({ title: "Erro", text: "Produto possui pedidos associados e não pode ser removido.", icon: "error" });
    }
  }

  const updateProduct = (updatedProduct) => {
    setProducts(prev => prev.map(p => p.id === updatedProduct.id ? updatedProduct : p));
  };

  return (
    <Box>
      {/* Section header */}
      <Flex align="center" justify="space-between" mb="20px">
        <Box>
          <Text fontSize="11px" fontWeight="800" color="gray.400" textTransform="uppercase" letterSpacing="0.08em">
            Catálogo
          </Text>
          <Text fontSize="20px" fontWeight="900" color={GREEN}>
            Produtos
            <Text as="span" fontSize="14px" fontWeight="600" color="gray.400" ml="8px">({products.length})</Text>
          </Text>
        </Box>
        <Button
          size="sm" borderRadius="8px" px="14px"
          bg={showForm ? "gray.100" : GREEN} color={showForm ? "gray.600" : ORANGE}
          fontWeight="700" fontSize="12px" _hover={{ opacity: 0.88 }}
          leftIcon={showForm ? <FiX size={13} /> : <FiPlus size={13} />}
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? "Cancelar" : "Novo produto"}
        </Button>
      </Flex>

      {/* Create form */}
      {showForm && (
        <Box
          bg="white" borderRadius="14px" p="20px" mb="20px"
          border="1px solid rgba(0,74,51,.12)" boxShadow="0 2px 10px rgba(0,74,51,.06)"
        >
          <Text fontSize="13px" fontWeight="800" color={GREEN} mb="16px">Cadastrar novo produto</Text>
          <Box mb="12px">
            <FormField label="Nome do produto">
              <Input placeholder="Nome" onChange={e => handleChange("name", e.target.value)} {...inputProps} />
            </FormField>
          </Box>
          <Flex gap="10px" mb="12px">
            <Box flex="2">
              <FormField label="Marca">
                <Input placeholder="Marca" onChange={e => handleChange("brand", e.target.value)} {...inputProps} />
              </FormField>
            </Box>
            <Box flex="1">
              <FormField label="Tamanho">
                <Input placeholder="ex: 750ml" onChange={e => handleChange("size", e.target.value)} {...inputProps} />
              </FormField>
            </Box>
            <Box flex="1">
              <FormField label="Qtd/caixa">
                <Input placeholder="12" type="number" onChange={e => handleChange("quantity_per_box", e.target.value)} {...inputProps} />
              </FormField>
            </Box>
          </Flex>
          <Flex gap="10px" mb="12px">
            <Box flex="2">
              <FormField label="Tipo de bebida">
                <Select placeholder="Selecione o tipo" onChange={e => handleChange("product_type_id", e.target.value)} {...inputProps}>
                  {productTypes.map(pt => (
                    <option key={pt.id} value={pt.id}>{pt.name}</option>
                  ))}
                </Select>
              </FormField>
            </Box>
            <Box flex="1">
              <FormField label="Preço de mercado">
                <Input placeholder="0.00" type="number" onChange={e => handleChange("market_price", e.target.value)} {...inputProps} />
              </FormField>
            </Box>
            <Box flex="1">
              <FormField label="Preço sugerido">
                <Input placeholder="0.00" type="number" onChange={e => handleChange("suggested_price_for_sale", e.target.value)} {...inputProps} />
              </FormField>
            </Box>
          </Flex>
          <Box mb="12px" maxW="260px">
            <FormField label="Volume (ml) — barril ou garrafa">
              <Input placeholder="ex: 20000 ou 750" type="number" onChange={e => handleChange("unit_volume_ml", e.target.value)} {...inputProps} />
            </FormField>
            <Text fontSize="11px" color="gray.400" mt="4px">
              Usado pela calculadora de eventos: volume do barril (Chope/Drink em Barril — 20L = 20000, 30L = 30000, 50L = 50000) ou da garrafa (Cerveja, Gin, Vodka, Cachaça — ex: 750, 1000).
            </Text>
          </Box>
          <Box mb="12px" maxW="340px">
            <FormField label="Categoria da calculadora (só este produto)">
              <Select value={product.event_drink_category} onChange={e => handleChange("event_drink_category", e.target.value)} {...inputProps}>
                <option value="">— Usar a categoria do Tipo de Produto —</option>
                {drinkCategories.map(dc => (
                  <option key={dc.key} value={dc.key}>{dc.label}</option>
                ))}
              </Select>
            </FormField>
            <Text fontSize="11px" color="gray.400" mt="4px">
              Só preencha se ESTE produto precisa de uma categoria diferente do Tipo dele — hoje o uso real é marcar só os
              2-3 produtos de Gin/Vodka/Cachaça/Licor escolhidos pela Boozen, deixando o Tipo sem categoria.
            </Text>
          </Box>
          <Box mb="12px">
            <Checkbox colorScheme="green" isChecked={!!product.is_official_kit} onChange={e => handleChange("is_official_kit", e.target.checked)}>
              <Text fontSize="13px" fontWeight="600">Kit oficial Boozen</Text>
            </Checkbox>
            <Text fontSize="11px" color="gray.400" mt="4px">Produtos marcados aparecem primeiro nas sugestões da calculadora de eventos.</Text>
          </Box>
          <Box mb="12px">
            <FormField label="Descrição">
              <Textarea
                placeholder="Descrição do produto"
                rows={3}
                onChange={e => handleChange("description", e.target.value)}
                {...inputProps}
                resize="vertical"
              />
            </FormField>
          </Box>
          <Box mb="16px">
            <FormField label="Review do cliente">
              <Textarea
                placeholder="Texto de review para o cliente"
                rows={3}
                onChange={e => handleChange("customer_review", e.target.value)}
                {...inputProps}
                resize="vertical"
              />
            </FormField>
          </Box>
          <Flex gap="10px" mb="16px">
            <Box>
              <Input type="file" id="newProductPhotos" display="none" multiple onChange={e => setProductPhotos(e.target.files)} />
              <label htmlFor="newProductPhotos">
                <Box as="span" display="inline-flex" alignItems="center" border={`1px solid ${GREEN}`} borderRadius="8px" px="12px" py="7px" cursor="pointer" fontSize="12px" fontWeight="700" color={GREEN} _hover={{ bg: GREEN, color: ORANGE }}>
                  Fotos {productPhotos?.length > 0 ? `(${productPhotos.length})` : ""}
                </Box>
              </label>
            </Box>
            <Box>
              <Input type="file" id="newProductFlyer" display="none" multiple={false} onChange={e => setProductFlyer(e.target.files[0])} />
              <label htmlFor="newProductFlyer">
                <Box as="span" display="inline-flex" alignItems="center" border={`1px solid ${GREEN}`} borderRadius="8px" px="12px" py="7px" cursor="pointer" fontSize="12px" fontWeight="700" color={GREEN} _hover={{ bg: GREEN, color: ORANGE }}>
                  Flyer {productFlyer ? `(${productFlyer.name.substring(0, 12)}…)` : ""}
                </Box>
              </label>
            </Box>
          </Flex>
          <Button bg={GREEN} color={ORANGE} borderRadius="8px" px="24px" fontWeight="700" fontSize="13px" _hover={{ opacity: 0.88 }} onClick={handleCreate}>
            Cadastrar produto
          </Button>
        </Box>
      )}

      {/* Search + filters */}
      <Flex align="center" gap="10px" mb="10px" wrap="wrap">
        <Box position="relative" flex="1" minW="220px" maxW="360px">
          <Box position="absolute" left="10px" top="50%" transform="translateY(-50%)" color="gray.400" pointerEvents="none">
            <FiSearch size={14} />
          </Box>
          <Input
            pl="32px"
            placeholder="Buscar por nome, marca, tamanho…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            {...inputProps}
            size="sm"
          />
        </Box>
        <Select
          placeholder="Subcategoria (todas)"
          value={subcategoryFilter}
          onChange={e => setSubcategoryFilter(e.target.value)}
          {...inputProps}
          size="sm"
          w="auto"
          minW="170px"
        >
          {(allSubcategories || []).map(s => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </Select>
        <Select
          placeholder="Categoria (todas)"
          value={categoryFilter}
          onChange={e => setCategoryFilter(e.target.value)}
          {...inputProps}
          size="sm"
          w="auto"
          minW="170px"
        >
          {categoryOptions.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </Select>
        <Select
          placeholder="Tipo (todos)"
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
          {...inputProps}
          size="sm"
          w="auto"
          minW="170px"
        >
          {typeOptions.map(pt => (
            <option key={pt.id} value={pt.id}>{pt.name}</option>
          ))}
        </Select>
        {(search || typeFilter || categoryFilter || subcategoryFilter) && (
          <Button size="sm" variant="ghost" color="gray.500" fontWeight="600" fontSize="12px" onClick={clearFilters}>
            Limpar filtros
          </Button>
        )}
      </Flex>
      <Text fontSize="12px" color="gray.400" mb="14px">
        {filteredProducts.length} de {products.length} produto{products.length !== 1 ? "s" : ""}
      </Text>

      {/* Table */}
      <Box bg="white" borderRadius="16px" border="1px solid rgba(0,74,51,.08)" boxShadow="0 2px 12px rgba(0,74,51,.05)" overflow="hidden">
        {filteredProducts.length === 0 ? (
          <Box py="48px" textAlign="center">
            <Text fontSize="15px" fontWeight="700" color="gray.400">
              {search ? `Nenhum produto encontrado para "${search}"` : "Nenhum produto encontrado com esses filtros"}
            </Text>
          </Box>
        ) : (
          <TableContainer>
            <Table size="sm" variant="simple">
              <Thead bg="rgba(0,74,51,.04)">
                <Tr>
                  <Th color={GREEN} fontSize="11px" py="12px">#</Th>
                  <Th color={GREEN} fontSize="11px">Nome</Th>
                  <Th color={GREEN} fontSize="11px">Marca</Th>
                  <Th color={GREEN} fontSize="11px">Tamanho</Th>
                  <Th color={GREEN} fontSize="11px">Qtd/cx</Th>
                  <Th color={GREEN} fontSize="11px">Tipo</Th>
                  <Th color={GREEN} fontSize="11px">Categoria</Th>
                  <Th color={GREEN} fontSize="11px">Subcategoria</Th>
                  <Th color={GREEN} fontSize="11px">Calculadora</Th>
                  <Th color={GREEN} fontSize="11px">Lote</Th>
                  <Th color={GREEN} fontSize="11px">Ações</Th>
                </Tr>
              </Thead>
              <Tbody>
                {pagedProducts.map(p => {
                  const { productType, category, subcategory, calculatorKey, calculatorSource } = resolveTaxonomy(p);
                  return (
                  <Tr key={p.id} _hover={{ bg: "rgba(0,74,51,.025)" }} transition="background 0.1s">
                    <Td py="10px">
                      <Text fontSize="12px" fontWeight="700" color={GREEN}>#{p.id}</Text>
                    </Td>
                    <Td>
                      <Text fontSize="12px" fontWeight="600" noOfLines={1} maxW="180px">{p.name}</Text>
                      {p.is_official_kit && (
                        <Badge mt="2px" fontSize="9px" borderRadius="4px" bg="rgba(252,143,4,.15)" color="#b56500">Kit oficial</Badge>
                      )}
                    </Td>
                    <Td>
                      <Text fontSize="12px" color="gray.600">{p.brand?.substring(0, 14)}</Text>
                    </Td>
                    <Td>
                      <Text fontSize="12px" color="gray.600">{p.size || "—"}</Text>
                    </Td>
                    <Td>
                      <Text fontSize="12px" color="gray.600">{p.quantity_per_box}</Text>
                    </Td>
                    <Td>
                      <Text fontSize="12px" color="gray.600" noOfLines={1} maxW="140px">{productType?.name || "—"}</Text>
                    </Td>
                    <Td>
                      <Text fontSize="12px" color="gray.600" noOfLines={1} maxW="120px">{category?.name || "—"}</Text>
                    </Td>
                    <Td>
                      <Text fontSize="12px" color="gray.600" noOfLines={1} maxW="120px">{subcategory?.name || "—"}</Text>
                    </Td>
                    <Td>
                      {calculatorKey ? (
                        <Box>
                          <Text
                            fontSize="10px" fontWeight="700" color={GREEN} bg="rgba(0,74,51,.08)"
                            borderRadius="99px" px="8px" py="2px" textTransform="uppercase" letterSpacing="0.03em"
                            display="inline-block"
                          >
                            {drinkCategoryLabel(calculatorKey)}
                          </Text>
                          <Text fontSize="9px" color="gray.400" mt="2px">
                            {calculatorSource === "produto" ? "definida no produto" : "herdada do Tipo"}
                          </Text>
                        </Box>
                      ) : (
                        <Text fontSize="10px" fontWeight="600" color="gray.300">
                          não aparece na calculadora
                        </Text>
                      )}
                    </Td>
                    <Td>
                      <BatchCard
                        productId={p.id}
                        productName={p.name}
                        allBatches={allBatches}
                        allPrices={allPrices?.filter(price => {
                          const productBatch = allBatches?.find(b => b.product_id === p.id);
                          return productBatch && price.batch_id === productBatch.id;
                        })}
                        productBatch={allBatches?.find(b => b.product_id === p.id)}
                      />
                    </Td>
                    <Td>
                      <Flex gap="6px">
                        <EditProduct
                          productId={p.id}
                          allProductTypes={productTypes}
                          allCategories={allCategories}
                          allSubcategories={allSubcategories}
                          allDrinkCategories={drinkCategories}
                          allBatches={allBatches}
                          allPrices={allPrices}
                          onUpdateProduct={updateProduct}
                          thisProduct={p}
                        />
                        <Button
                          size="xs" borderRadius="7px" px="8px" h="26px"
                          bg="rgba(231,76,60,.08)" color="#e74c3c" border="1px solid rgba(231,76,60,.25)"
                          fontWeight="700" fontSize="11px" _hover={{ bg: "rgba(231,76,60,.15)" }}
                          onClick={e => handleRemove(e, p.id)}
                        >
                          <FiTrash2 size={12} />
                        </Button>
                      </Flex>
                    </Td>
                  </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
        )}
        <Flex
          align="center" justify="space-between" px="20px" py="10px"
          borderTop="1px solid rgba(0,74,51,.06)" wrap="wrap" gap="8px"
        >
          <Text fontSize="12px" color="gray.400">
            {products.length} produto{products.length !== 1 ? "s" : ""} cadastrado{products.length !== 1 ? "s" : ""}
          </Text>
          {filteredProducts.length > 0 && (
            <Flex align="center" gap="10px">
              <Button
                size="xs" variant="ghost" color={GREEN} fontWeight="700" fontSize="11px"
                isDisabled={currentPage <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}
              >
                Anterior
              </Button>
              <Text fontSize="12px" color="gray.500" fontWeight="600">
                Página {currentPage} de {totalPages}
              </Text>
              <Button
                size="xs" variant="ghost" color={GREEN} fontWeight="700" fontSize="11px"
                isDisabled={currentPage >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              >
                Próxima
              </Button>
            </Flex>
          )}
        </Flex>
      </Box>
    </Box>
  );
}
